/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdminCompany } from '../models/AdminCompany';
import type { CadoozOrderRequest } from '../models/CadoozOrderRequest';
import type { CadoozProduct } from '../models/CadoozProduct';
import type { PaginatedAdminCompaniesList } from '../models/PaginatedAdminCompaniesList';
import type { PaginatedCadoozProductList } from '../models/PaginatedCadoozProductList';
import type { PaginatedCompanySachbezugList } from '../models/PaginatedCompanySachbezugList';
import type { PaginatedSalaryComponentList } from '../models/PaginatedSalaryComponentList';
import type { PaginatedVoucherUserList } from '../models/PaginatedVoucherUserList';
import type { SalaryComponent } from '../models/SalaryComponent';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AdminService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create a new Cadooz order
     * @returns any No response body
     * @throws ApiError
     */
    public adminCadoozBaukingOrdersCreate({
        requestBody,
    }: {
        requestBody: CadoozOrderRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/admin/cadooz/bauking/orders/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get a Cadooz order by its order number
     * @returns any No response body
     * @throws ApiError
     */
    public adminCadoozBaukingOrdersStatusRetrieve({
        orderNumber,
    }: {
        /**
         * The Cadooz order number
         */
        orderNumber: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/cadooz/bauking/orders/status/',
            query: {
                'order_number': orderNumber,
            },
        });
    }

    /**
     * Get the vouchers of a Cadooz order
     * @returns any No response body
     * @throws ApiError
     */
    public adminCadoozBaukingOrdersVouchersRetrieve({
        orderNumber,
    }: {
        /**
         * The Cadooz order number
         */
        orderNumber: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/cadooz/bauking/orders/vouchers/',
            query: {
                'order_number': orderNumber,
            },
        });
    }

    /**
     * Get cadooz products
     * @returns PaginatedCadoozProductList
     * @throws ApiError
     */
    public adminCadoozProductsList({
        categories,
        limit,
        offset,
        voucherSelection,
    }: {
        /**
         * * `clothing_accessories` - clothing_accessories
         * * `car_mobility` - car_mobility
         * * `beauty_wellness` - beauty_wellness
         * * `books_music` - books_music
         * * `grocery` - grocery
         * * `hobby_travel` - hobby_travel
         * * `multimedia_electronics` - multimedia_electronics
         * * `toys_baby` - toys_baby
         * * `sports_lifestyle` - sports_lifestyle
         * * `streaming_gaming` - streaming_gaming
         * * `living_garden` - living_garden
         */
        categories?: Array<'beauty_wellness' | 'books_music' | 'car_mobility' | 'clothing_accessories' | 'grocery' | 'hobby_travel' | 'living_garden' | 'multimedia_electronics' | 'sports_lifestyle' | 'streaming_gaming' | 'toys_baby'>,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        voucherSelection?: boolean,
    }): CancelablePromise<PaginatedCadoozProductList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/cadooz/products/',
            query: {
                'categories': categories,
                'limit': limit,
                'offset': offset,
                'voucher_selection': voucherSelection,
            },
        });
    }

    /**
     * Get cadooz product
     * @returns CadoozProduct
     * @throws ApiError
     */
    public adminCadoozProductsRetrieve({
        id,
    }: {
        /**
         * A unique value identifying this cadooz product.
         */
        id: string,
    }): CancelablePromise<CadoozProduct> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/cadooz/products/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Returns all users (ID, Name, E-Mail) who have an EmployeeVoucherOrder in the specified month and for the specified product (Voucher Product ID).
     * @returns PaginatedVoucherUserList
     * @throws ApiError
     */
    public adminCadoozProductsUsersWithVoucherList({
        month,
        voucherId,
        year,
        categories,
        limit,
        offset,
        voucherSelection,
    }: {
        /**
         * Monat des Abrechnungsmonats (1-12)
         */
        month: number,
        /**
         * ID der Variante des Cadooz-Produkts (Voucher)
         */
        voucherId: string,
        /**
         * Jahr des Abrechnungsmonats (z.B. 2025)
         */
        year: number,
        /**
         * * `clothing_accessories` - clothing_accessories
         * * `car_mobility` - car_mobility
         * * `beauty_wellness` - beauty_wellness
         * * `books_music` - books_music
         * * `grocery` - grocery
         * * `hobby_travel` - hobby_travel
         * * `multimedia_electronics` - multimedia_electronics
         * * `toys_baby` - toys_baby
         * * `sports_lifestyle` - sports_lifestyle
         * * `streaming_gaming` - streaming_gaming
         * * `living_garden` - living_garden
         */
        categories?: Array<'beauty_wellness' | 'books_music' | 'car_mobility' | 'clothing_accessories' | 'grocery' | 'hobby_travel' | 'living_garden' | 'multimedia_electronics' | 'sports_lifestyle' | 'streaming_gaming' | 'toys_baby'>,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        voucherSelection?: boolean,
    }): CancelablePromise<PaginatedVoucherUserList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/cadooz/products/users-with-voucher/',
            query: {
                'categories': categories,
                'limit': limit,
                'month': month,
                'offset': offset,
                'voucher_id': voucherId,
                'voucher_selection': voucherSelection,
                'year': year,
            },
        });
    }

    /**
     * Get all companies
     * @returns PaginatedAdminCompaniesList
     * @throws ApiError
     */
    public adminCompaniesList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedAdminCompaniesList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/companies/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Get a specific company
     * @returns AdminCompany
     * @throws ApiError
     */
    public adminCompaniesRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
    }): CancelablePromise<AdminCompany> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/companies/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Get companies with active Sachbezug benefit for a given month
     * @returns PaginatedCompanySachbezugList
     * @throws ApiError
     */
    public adminCompaniesWithSachbezugList({
        month,
        limit,
        offset,
    }: {
        /**
         * Abrechnungszeitraum im Format YYYY-MM (z.B. 2026-03)
         */
        month: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedCompanySachbezugList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/companies/with-sachbezug/',
            query: {
                'limit': limit,
                'month': month,
                'offset': offset,
            },
        });
    }

    /**
     * Get all salary components
     * @returns PaginatedSalaryComponentList
     * @throws ApiError
     */
    public adminSalaryComponentsList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedSalaryComponentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/salary-components/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Get a specific salary component
     * @returns SalaryComponent
     * @throws ApiError
     */
    public adminSalaryComponentsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this salary component.
         */
        id: string,
    }): CancelablePromise<SalaryComponent> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/salary-components/{id}/',
            path: {
                'id': id,
            },
        });
    }

}
